import React from 'react';
import { useDebounce } from 'react-use';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { Geolocation } from 'talent_matching/types';
import { SimpleSingleDropdown } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import RadioGroup from '@grnhse/seedling/lib/birch/components/radio_group';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { withLocalePrefix } from 'shared/utils/translation';
import { LocationInput } from '../../../shared/geocoding/location_input';
import { LocationControlResult } from '@grnhse/location-control';

const t = withLocalePrefix('talent_matching.filter_row.location');

const RADIUS_OPTIONS = ['5', '10', '25', '50', '100'];

type LocationContentProps = {
  readOnly?: boolean;
};

export const LocationContent = ({ readOnly = false }: LocationContentProps) => {
  const { setValue, watch } = useFormContext();

  const locationFilterType: 'name' | 'radius' = watch('locationFilterType');
  const locationName: string = watch('locationName');
  const locationRadius: string | null = watch('geolocationRadius');
  const selectedLocation: LocationControlResult | null = watch('geolocation');

  useDebounce(
    () => {
      setValue('debouncedLocationName', locationName, { shouldDirty: true });
    },
    500,
    [locationName]
  );

  const setLocationFilterType = (type: 'name' | 'radius') => {
    if (readOnly) {
      return;
    }
    setValue('locationFilterType', type, { shouldDirty: true });
  };

  const radiusOptions = RADIUS_OPTIONS.map((radius) => {
    return {
      label: t('radius', { radius: radius }),
      value: radius,
    };
  });

  const selectedRadius = radiusOptions.find(
    (option) => option.value === locationRadius
  ) as Item;

  const onSelectedLocationChange = (
    updatedLocation: LocationControlResult | null
  ) => {
    if (readOnly) {
      return;
    }

    const mappedLocation = !updatedLocation
      ? null
      : {
          city: updatedLocation.city,
          displayName: updatedLocation.displayName,
          latitude: updatedLocation.latitude,
          longitude: updatedLocation.longitude,
          location: updatedLocation.displayName,
          label: updatedLocation.displayName,
          value: [updatedLocation.longitude, updatedLocation.latitude].join(
            ','
          ),
        };

    setValue('geolocation', mappedLocation, { shouldDirty: true });
  };

  const radioOptions = [
    {
      label: t('by_radius'),
      value: 'radius',
    },
    {
      label: t('by_name'),
      value: 'name',
    },
  ];

  return (
    <Stack spacing={2} direction="column">
      <RadioGroup
        ariaLabel={t('location_filter_type')}
        options={radioOptions}
        value={locationFilterType}
        onChange={(selected) =>
          setLocationFilterType(selected as 'radius' | 'name')
        }
        direction="row"
        disabled={readOnly}
      />
      {locationFilterType === 'radius' ? (
        <Stack spacing={2} direction="row" sx={{ width: '100%' }}>
          <Grid item xs={6} sx={{ minWidth: 0 }}>
            <SimpleSingleDropdown
              label={t('radius_label')}
              clearable={false}
              items={radiusOptions}
              selectedItem={selectedRadius}
              onSelectedItemChange={(radius) => {
                if (readOnly) {
                  return;
                }
                setValue('geolocationRadius', radius.value, {
                  shouldDirty: false,
                });
              }}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={6} sx={{ minWidth: 0 }}>
            <LocationInput
              disabled={readOnly}
              label={t('radius_location_label')}
              location={selectedLocation}
              onChange={onSelectedLocationChange}
            />
          </Grid>
        </Stack>
      ) : (
        <TextInput
          label={t('location_label')}
          value={locationName}
          onChange={(e) => {
            if (readOnly) {
              return;
            }
            setValue('locationName', e.target.value, { shouldDirty: true });
          }}
          disabled={readOnly}
        />
      )}
    </Stack>
  );
};
